import {createColumnHelper} from "@tanstack/react-table";
import i18next from "i18next";
import {useTranslation} from "react-i18next";
import {truncateString} from "../../../Utils/StringHelper";
import {TanStackTable} from "../TanStackTable";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight} from "@fortawesome/free-solid-svg-icons";
import * as React from "react";
import {useState, useEffect} from "react";
import dayjs from "dayjs";
import {useBoundStore} from "../../../Store/store";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import {DateTimePickerInput} from "../../Form/DateTimePickerInput";
import {BaseCard} from "../../Cards/BaseCard";

export const ShiftsTable = (props) => {
    const {
        userId,
        branchId,
        forceNewData = false
    } = props;

    const [shifts, setShifts] = useState([]);
    const [metaData, setMetaData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [sortBy, setSortBy] = useState('start_time');
    const [sortOrder, setSortOrder] = useState('asc');

    const getShiftsForUser = useBoundStore((state) => state.getShiftsForUser);
    const {t} = useTranslation();

    const schema = yup.object({
        start_time: yup.date().nullable(),
        end_time: yup.date().nullable(),
    }).required();

    const {control, setValue, watch, register, formState: {errors}} = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            start_time: null,
            end_time: null,
        }
    });

    const startTime = watch('start_time');
    const endTime = watch('end_time');

    useEffect(() => {
        const fetchShifts = async () => {
            setLoading(true);
            const start = startTime ? dayjs(startTime).unix() : null;
            const end = endTime ? dayjs(endTime).unix() : null;

            const result = await getShiftsForUser(userId, currentPage, start, end, sortBy, sortOrder, branchId);

            if (result) {
                setShifts(result.data);
                setMetaData(result.meta);
            }
            setLoading(false);
            setInitialLoad(false);
        };

        if (userId || branchId) {
            fetchShifts();
        }
    }, [userId, branchId, currentPage, forceNewData, getShiftsForUser, startTime, endTime, sortBy, sortOrder]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('start_time', {
            header: t('tables.start'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY HH:mm'),
            enableSorting: true,
        }),
        columnHelper.accessor('end_time', {
            header: t('tables.end'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY HH:mm'),
            enableSorting: true,
        }),
        columnHelper.accessor('is_published', {
            header: t('tables.published'),
            enableSorting: false,
            cell: props => props.cell.getValue() ? t('general.labels.yes') : t('general.labels.no'),
        }),
        columnHelper.accessor('branch', {
            header: t('tables.customer'),
            enableSorting: false,
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('freelancer', {
            header: i18next.t('shifts_module.column_freelancer'),
            enableSorting: false,
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name ?? ''}`),
        }),
        columnHelper.accessor('confirmed_applied_as_statuut', {
            header: t('shifts_module.column_confirmed_statuut'),
            enableSorting: false,
            cell: props => {
                const s = props.cell.getValue();
                return s ? t(`statuten.${s}`, s) : '-';
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            enableSorting: false,
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <Link
                            to={`/admin/shifts/${props.row.original.id}`}
                            className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                        </Link>
                    </div>
                )
            },
        })
    ];

    if (initialLoad && loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div>
            <BaseCard border={false} className={'mb-4'}>
                <div className={'grid grid-cols-1 md:grid-cols-2 gap-4'}>
                    <DateTimePickerInput
                        label={'Start time'}
                        name={'start_time'}
                        register={register}
                        control={control}
                        setValue={setValue}
                        dateFormat={'dd/MM/yyyy'}
                        showTimeInput={false}
                        isClearable={true}
                        translationIndex={'general.fields.start_time'}
                        errors={errors}
                    />
                    <DateTimePickerInput
                        label={'End time'}
                        name={'end_time'}
                        dateFormat={'dd/MM/yyyy'}
                        register={register}
                        control={control}
                        setValue={setValue}
                        showTimeInput={false}
                        isClearable={true}
                        translationIndex={'general.fields.end_time'}
                        errors={errors}
                    />
                </div>
            </BaseCard>

            {!initialLoad && shifts.length === 0 && !loading ? (
                <div className="text-center text-gray-500 py-8">
                    {t('shifts.no_results')}
                </div>
            ) : (
                <div className="relative">
                    {loading && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-10">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    )}
                    <TanStackTable
                        columns={columns}
                        tableData={shifts}
                        forceNewData={forceNewData}
                        laravelPaginationMetaData={metaData}
                        onChangePage={(page) => setCurrentPage(page + 1)}
                        onSortingChange={(columnId, order) => {
                            setSortBy(columnId);
                            setSortOrder(order);
                            setCurrentPage(1);
                        }}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                    />
                </div>
            )}
        </div>
    )
}